import { useEffect, useRef } from "react";
import {
  GOOGLE,
  ALERT_ICON_TYPE_SUCCESS,
  ADS_GOOGLE_SEARCH_AD_GROUP_TYPE_STANDARD_OPTION,
  ADS_GOOGLE_SEARCH_AD_GROUP_TYPE_DSA_OPTION,
  getGoogleSearchAdGroupType,
  ADS_CAMPAIGN_STATUS_DRAFT,
} from "../../../../utils/constants";
import Grid from "@mui/material/Grid";
import Select from "../../../Form/Select";
import KeywordsGoogleContainer from "../../../Google/KeywordsGoogleContainer";
import {
  FIELD_KEYWORDS_REPEATED,
  FIELD_REQUIRED,
  GOOGLE_KEYWORDS_EXPLANATION,
  GOOGLE_KEYWORDS_NEGATIVE_EXPLANATION,
  KEYWORDS_NEGATIVE_PLACEHOLDER,
  KEYWORDS_PLACEHOLDER,
  COPY_VALUE_IN_OTHER_ADGROUPS,
  READY,
  GOOGLE_SEARCH_AD_GROUP_TYPE_LABEL,
} from "../../../../i18n/keysTranslations";
import { getDuplicateItemsArray } from "../../../../utils/array";
import { useFormContext } from "react-hook-form";
import useFocusErrorForm from "../../../../hooks/useFocusErrorForm";
import { useTranslationApp } from "../../../../lib/i18next";
import CustomAudiencesContainerGoogle from "../Common/CustomAudiencesContainerGoogle";
import ClickableText from "../../../Texts/ClickableText";
import { SimpleAlert } from "../../../Alerts/Alerts";
import { optionsGoogleSearchAdGroupType } from "../../../../utils/options";
import WebpagesGoogleContainer from "../../../Google/Webpages/WebpagesGoogleContainer";
import _ from "lodash";
import { getUniqueID } from "../../../../utils/numbers";

const platform = GOOGLE;
const grid = {
  xs: 12,
};

const AdGroupSearchGoogleForm = () => {
  const {
    control,
    formState,
    watch,
    sectionSelected,
    isGeneralDisabledFields,
    getValues,
    setValue,
  } = useFormContext();
  useFocusErrorForm(formState);

  const errors = formState.errors;

  const { t } = useTranslationApp();

  const platformData = watch(`platformsData.${platform}`) || {};
  const isIncompatibleCampaign = platformData?.isIncompatible;

  const adsGroups = watch("adsGroups");
  const adGroupID = sectionSelected.adGroupID;
  const index = adsGroups.findIndex((item) => item.id === adGroupID);
  const BASIC_PATH = `adsGroups.[${index}]`;
  const isIncompatibleAdGroup = watch(`${BASIC_PATH}.isIncompatible`) || false;
  const language = watch("language");
  const status = watch(`${BASIC_PATH}.status`) || ADS_CAMPAIGN_STATUS_DRAFT;
  const isAdGroupCreated = status !== ADS_CAMPAIGN_STATUS_DRAFT;
  const searchAdGroupTypeRaw = watch(`${BASIC_PATH}.type`);
  const type = getGoogleSearchAdGroupType({
    type: searchAdGroupTypeRaw,
  });
  const isDsa = type === ADS_GOOGLE_SEARCH_AD_GROUP_TYPE_DSA_OPTION;

  const lastSearchAdGroupTypeRef = useRef(type);

  useEffect(() => {
    lastSearchAdGroupTypeRef.current = type;
  }, [type]);

  const isDisabledFields =
    isGeneralDisabledFields || isIncompatibleCampaign || isIncompatibleAdGroup;

  useEffect(() => {
    if (index < 0) {
      return;
    }
    const raw = getValues(`${BASIC_PATH}.type`);
    if (raw === undefined || raw === "") {
      setValue(
        `${BASIC_PATH}.type`,
        ADS_GOOGLE_SEARCH_AD_GROUP_TYPE_STANDARD_OPTION
      );
    }
  }, [index, BASIC_PATH, getValues, setValue]);

  const onCopyValuesInOtherAds = ({ type }) => {
    const value = getValues(`${BASIC_PATH}.${type}`);
    const adsGroupsValues = getValues("adsGroups") || [];

    adsGroupsValues.forEach((adGroup, currentIndexAdGroup) => {
      if (adGroup.platform !== platform) return;

      setValue(`adsGroups.[${currentIndexAdGroup}].${type}`, value);
    });

    SimpleAlert({
      title: t(READY),
      icon: ALERT_ICON_TYPE_SUCCESS,
    });
  };

  // Runs after Form Select updates the field; use ref because getValues already has nextType.
  const onSearchAdGroupTypeSideEffects = (nextType) => {
    const prev = lastSearchAdGroupTypeRef.current;
    if (prev === nextType) {
      return;
    }
    if (nextType === ADS_GOOGLE_SEARCH_AD_GROUP_TYPE_DSA_OPTION) {
      setValue(`${BASIC_PATH}.keywords`, []);

      const ads = getValues(`${BASIC_PATH}.ads`) || [];
      ads.forEach((ad, index) => {
        setValue(
          `${BASIC_PATH}.ads.[${index}].descriptions`,
          _.isEmpty(ad.descriptions)
            ? [{ value: "", id: getUniqueID() }]
            : ad.descriptions.slice(0, 2)
        );
      });
    } else {
      setValue(`${BASIC_PATH}.webpages`, []);
    }
  };

  return (
    <Grid container spacing={2}>
      {index >= 0 && (
        <Grid item xs={12}>
          <Select
            control={control}
            errors={errors}
            name={`${BASIC_PATH}.type`}
            label={t(GOOGLE_SEARCH_AD_GROUP_TYPE_LABEL)}
            variant="filled"
            fullWidth
            disabled={isDisabledFields || isAdGroupCreated}
            options={optionsGoogleSearchAdGroupType}
            doTranslate
            value={ADS_GOOGLE_SEARCH_AD_GROUP_TYPE_STANDARD_OPTION}
            rules={{ required: false }}
            idLabel={`google-search-ad-group-type-${adGroupID}`}
            actionsChange={onSearchAdGroupTypeSideEffects}
          />
        </Grid>
      )}
      <Grid item xs={12}>
        <CustomAudiencesContainerGoogle
          name={`${BASIC_PATH}.customAudiences`}
          control={control}
          disabled={isDisabledFields}
          from="group"
          afterTitle={
            <ClickableText
              disabled={isDisabledFields}
              onClick={() =>
                onCopyValuesInOtherAds({ type: "customAudiences" })
              }
            >
              {t(COPY_VALUE_IN_OTHER_ADGROUPS)}
            </ClickableText>
          }
        />
      </Grid>
      {!isDsa && (
        <Grid item {...grid}>
          <KeywordsGoogleContainer
            label={t(KEYWORDS_PLACEHOLDER)}
            name={`${BASIC_PATH}.keywords`}
            explanation={GOOGLE_KEYWORDS_EXPLANATION}
            variant="filled"
            fullWidth
            disabled={isDisabledFields}
            isNegativeKeywords={false}
            adAccountID={platformData?.adAccountID}
            integrationID={platformData?.integrationID}
            language={language}
            control={control}
            errors={errors}
            afterTitle={
              <ClickableText
                disabled={isDisabledFields}
                onClick={() => onCopyValuesInOtherAds({ type: "keywords" })}
              >
                {t(COPY_VALUE_IN_OTHER_ADGROUPS)}
              </ClickableText>
            }
            rules={{
              required: {
                value: false,
                message: t(FIELD_REQUIRED),
              },
              validate: (value) => {
                if (!value || value.length === 0) {
                  return t(FIELD_REQUIRED);
                }
                const duplicate = getDuplicateItemsArray(value);
                if (duplicate.length > 0) {
                  return t(FIELD_KEYWORDS_REPEATED, {
                    value: duplicate.join(", "),
                  });
                }
              },
            }}
          />
        </Grid>
      )}
      {isDsa && (
        <Grid item {...grid}>
          <WebpagesGoogleContainer
            name={`${BASIC_PATH}.webpages`}
            control={control}
            errors={errors}
            disabled={isDisabledFields}
            afterTitle={
              <ClickableText
                disabled={isDisabledFields}
                onClick={() => onCopyValuesInOtherAds({ type: "webpages" })}
              >
                {t(COPY_VALUE_IN_OTHER_ADGROUPS)}
              </ClickableText>
            }
          />
        </Grid>
      )}
      <Grid item {...grid}>
        <KeywordsGoogleContainer
          isOptional={true}
          label={t(KEYWORDS_NEGATIVE_PLACEHOLDER)}
          explanation={GOOGLE_KEYWORDS_NEGATIVE_EXPLANATION}
          name={`${BASIC_PATH}.keywordsNegative`}
          variant="filled"
          fullWidth
          disabled={isDisabledFields}
          isNegativeKeywords={true}
          adAccountID={platformData?.adAccountID}
          integrationID={platformData?.integrationID}
          language={language}
          control={control}
          errors={errors}
          afterTitle={
            <ClickableText
              disabled={isDisabledFields}
              onClick={() =>
                onCopyValuesInOtherAds({ type: "keywordsNegative" })
              }
            >
              {t(COPY_VALUE_IN_OTHER_ADGROUPS)}
            </ClickableText>
          }
          rules={{
            required: {
              value: false,
              message: t(FIELD_REQUIRED),
            },
            validate: (value) => {
              if (!value || value.length === 0) {
                return true;
              }
              const duplicate = getDuplicateItemsArray(value);
              if (duplicate.length > 0) {
                return t(FIELD_KEYWORDS_REPEATED, {
                  value: duplicate.join(", "),
                });
              }
            },
          }}
        />
      </Grid>
    </Grid>
  );
};

export default AdGroupSearchGoogleForm;
